import { Checkbox } from '../../../components/Checkbox';
import { TextInput } from '../../../components/TextInput';
import type { SampleRosterBoard, SampleRosterPlacement } from '../../../types/sampleData';
import { KeyedSelect, NumberField, SampleFieldset, SampleRow } from './SampleFormControls';
import {
  fieldId,
  playerOptions,
  POSITION_CHOICES,
  removeAt,
  replaceAt,
  type SampleSectionProps,
} from './templateEditing';

export const RosterBoardFieldset = ({ value, onChange, errorFor }: SampleSectionProps) => {
  const board = value.rosterBoard;
  const players = playerOptions(value.players);
  const setBoard = (next: SampleRosterBoard | null) => onChange({ ...value, rosterBoard: next });
  const updatePlacement = (placements: SampleRosterPlacement[]) =>
    board && setBoard({ ...board, placements });

  return (
    <SampleFieldset
      legend="Roster Planning board"
      addLabel={board ? 'Add placement' : undefined}
      addTestId="sample-data-add-placement"
      onAdd={() =>
        board &&
        updatePlacement([
          ...board.placements,
          { playerKey: '', position: '', sortOrder: board.placements.length },
        ])
      }
    >
      <Checkbox
        label="Include a Roster Planning board"
        checked={board !== null}
        data-testid="sample-data-include-roster-board"
        onChange={(e) => setBoard(e.target.checked ? { name: '', placements: [] } : null)}
      />
      {board && (
        <TextInput
          id={fieldId('rosterBoard.name')}
          className="sample-form__field"
          label="Board name"
          value={board.name}
          error={errorFor('rosterBoard.name')}
          data-testid="sample-data-roster-board-name"
          onChange={(e) => setBoard({ ...board, name: e.target.value })}
        />
      )}
      {board?.placements.map((placement, index) => {
        const path = (field: string) => `rosterBoard.placements[${index}].${field}`;
        const patch = (next: Partial<SampleRosterPlacement>) =>
          updatePlacement(replaceAt(board.placements, index, next));
        return (
          <SampleRow
            // Placements carry no key of their own; the index is their identity in the payload too.
            key={index}
            label={`Placement ${index + 1}`}
            removeLabel={`Remove placement ${index + 1}`}
            removeTestId={`sample-data-remove-placement-${index}`}
            onRemove={() => updatePlacement(removeAt(board.placements, index))}
          >
            <KeyedSelect
              label="Player"
              path={path('playerKey')}
              error={errorFor(path('playerKey'))}
              testId={`sample-data-placement-player-${index}`}
              options={players}
              value={placement.playerKey}
              placeholder="Choose a player"
              onChange={(playerKey) => patch({ playerKey })}
            />
            <KeyedSelect
              label="Position"
              path={path('position')}
              error={errorFor(path('position'))}
              testId={`sample-data-placement-position-${index}`}
              options={POSITION_CHOICES}
              value={placement.position}
              placeholder="Choose a position"
              onChange={(position) => patch({ position })}
            />
            <NumberField
              label="Sort order"
              path={path('sortOrder')}
              error={errorFor(path('sortOrder'))}
              testId={`sample-data-placement-sortOrder-${index}`}
              value={placement.sortOrder}
              min={0}
              onChange={(sortOrder) => patch({ sortOrder: sortOrder ?? 0 })}
            />
          </SampleRow>
        );
      })}
    </SampleFieldset>
  );
};
